import { useEffect, useState } from 'react'
import { Box, Drawer, Flex, Portal, Text, VStack, toaster, useT } from '@minetrace/ui'
import { LuDownload, LuFileText, LuX } from 'react-icons/lu'
import {
  INDICE_MODO_DIGITALIZADO_SIN_FEDATARIO,
  type IndiceEntradaResponse,
} from '../../../api/models/response/indiceDigital'
import { expedienteService } from '../../../api/services'
import { brand, m3, themeColors } from '../../../theme'
import { formatDate } from '../../../lib/dates'
import { Field } from './ui'
import { downloadAuthenticated, folioRange, formatBytes } from './helpers'
import { OrigenBadge } from './badges'

/**
 * Detalle de una entrada foliada. ES UN `Drawer`, NO UN `Modal`, y por partida doble:
 * el detalle es lateral y contextual (la tabla sigue a la vista), y el overlay se
 * monta CERRADO y se abre al frame siguiente.
 *
 * Ese segundo punto no es estilo: montar un overlay de Chakra/zag ya-abierto aplica
 * el scroll-lock y el `aria-hidden` en el montaje, pero ese ciclo no se empareja con
 * el teardown y deja `<body>` con `pointer-events:none` y `#root` inerte — la página
 * "congelada". Al cerrar hay que pasar por `open=false` antes de desmontar, que es el
 * camino que sí libera el lock. Mismo patrón que `packages/ui/composed/Modal.tsx`.
 */
export function EntradaDrawer({
  entrada,
  expedienteCode,
  onClose,
}: {
  entrada: IndiceEntradaResponse | null
  expedienteCode: string
  onClose: () => void
}) {
  const { t } = useT()

  const [mounted, setMounted] = useState(false)
  const [active, setActive] = useState(false)
  // Se conserva la última entrada para que el contenido no parpadee a vacío durante
  // la animación de cierre.
  const [shown, setShown] = useState<IndiceEntradaResponse | null>(null)

  // TODOS los setState van dentro de un callback (rAF o timeout), nunca en el cuerpo
  // del efecto: además de ser lo que exige `react-hooks/set-state-in-effect`, es
  // justamente lo que hace falta aquí — el montaje y la apertura tienen que caer en
  // frames distintos para que zag empareje el scroll-lock con su teardown.
  useEffect(() => {
    if (!entrada) {
      const raf = requestAnimationFrame(() => setActive(false))
      const timer = setTimeout(() => {
        setMounted(false)
        setShown(null)
      }, 250)
      return () => { cancelAnimationFrame(raf); clearTimeout(timer) }
    }

    let openRaf = 0
    const mountRaf = requestAnimationFrame(() => {
      setShown(entrada)
      setMounted(true)
      openRaf = requestAnimationFrame(() => setActive(true))
    })
    return () => { cancelAnimationFrame(mountRaf); cancelAnimationFrame(openRaf) }
  }, [entrada])

  if (!mounted || !shown) return null

  const handleDownload = async () => {
    if (!shown.tieneArchivo) return
    try {
      await downloadAuthenticated(
        expedienteService.getItemFileUrl(expedienteCode, shown.itemId),
        shown.nombreArchivo ?? `${expedienteCode}_${shown.orden}`,
      )
    } catch (e) {
      toaster.error({
        description: e instanceof Error ? e.message : 'No se pudo descargar el archivo',
      })
    }
  }

  return (
    <Drawer.Root
      open={active}
      onOpenChange={(e) => { if (!e.open) onClose() }}
      placement="end"
      size="md"
    >
      <Portal>
        <Drawer.Backdrop bg="blackAlpha.400" />
        <Drawer.Positioner>
          <Drawer.Content bg={m3.surfaceContainerLowest}>
            <Flex
              alignItems="flex-start"
              justifyContent="space-between"
              gap={3}
              px={6}
              py={4}
              borderBottom="1px solid"
              borderColor={themeColors.border}
            >
              <Box minW={0}>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color={themeColors.textMuted}
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  {t('expedientes.electronico.entrada.title')}
                </Text>
                <Text fontSize="lg" fontWeight="700" color={themeColors.text} lineHeight="1.25">
                  {shown.titulo}
                </Text>
              </Box>
              <Box
                as="button"
                type="button"
                p={1.5}
                borderRadius="md"
                color={themeColors.textMuted}
                cursor="pointer"
                flexShrink={0}
                _hover={{ bg: themeColors.bgHover, color: themeColors.text }}
                onClick={onClose}
                aria-label={t('expedientes.electronico.entrada.close')}
              >
                <LuX size={18} />
              </Box>
            </Flex>

            <Box px={6} py={5} overflowY="auto">
              <VStack alignItems="stretch" gap={4}>
                {shown.descripcion && (
                  <Text fontSize="sm" color={themeColors.textSecondary}>{shown.descripcion}</Text>
                )}

                <Flex gap={6} flexWrap="wrap">
                  <Field
                    label={t('expedientes.electronico.entrada.orden')}
                    value={shown.orden}
                    mono
                  />
                  <Field
                    label={t('expedientes.electronico.entrada.folios')}
                    value={`${folioRange(shown.folioDesde, shown.folioHasta)} (${shown.folios})`}
                    mono
                  />
                  <Field
                    label={t('expedientes.electronico.entrada.produccion')}
                    value={formatDate(shown.fechaProduccion)}
                  />
                  <Field
                    label={t('expedientes.electronico.table.fechaExpediente')}
                    value={shown.fechaIncorporacion ? formatDate(shown.fechaIncorporacion) : '—'}
                  />
                </Flex>

                {/* La serie documental y su retencion no se muestran: la vigencia es
                    del expediente completo, no de cada papel que se le adjunta. El
                    dato sigue viajando en la respuesta; queda pendiente retirarlo
                    del modelo. */}
                <Box>
                  <Text
                    fontSize="10px"
                    fontWeight="700"
                    color={themeColors.textMuted}
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    mb={1.5}
                  >
                    {t('expedientes.electronico.table.tipo')}
                  </Text>
                  <Flex gap={2} flexWrap="wrap" alignItems="center">
                    <OrigenBadge modo={shown.modo} />
                  </Flex>
                </Box>

                <Field label={t('expedientes.electronico.entrada.tipo')} value={shown.itemTypeNombre} />

                {/* Modo de incorporación con su límite legal declarado en el sitio,
                    no escondido en una nota al pie. */}
                {shown.modo === INDICE_MODO_DIGITALIZADO_SIN_FEDATARIO && (
                  <Box
                    p={3}
                    borderRadius="lg"
                    bg={themeColors.bgCard}
                    borderLeft="3px solid"
                    borderColor={themeColors.borderHover}
                  >
                    <Text fontSize="xs" color={themeColors.textSecondary} lineHeight="1.6">
                      {t('expedientes.electronico.origen.copiaTooltip')}
                    </Text>
                  </Box>
                )}

                <Field
                  label={t('expedientes.electronico.entrada.hash')}
                  value={shown.resumenHash}
                  mono
                />

                <Box borderTop="1px solid" borderColor={themeColors.border} pt={4}>
                  {shown.tieneArchivo ? (
                    <VStack alignItems="stretch" gap={3}>
                      <Flex gap={6} flexWrap="wrap">
                        <Field
                          label={t('expedientes.electronico.entrada.file')}
                          value={shown.nombreArchivo ?? '—'}
                        />
                        <Field
                          label={t('expedientes.electronico.entrada.format')}
                          value={shown.formato ?? '—'}
                        />
                        <Field
                          label={t('expedientes.electronico.entrada.size')}
                          value={formatBytes(shown.tamanoBytes)}
                        />
                      </Flex>
                      <Box
                        as="button"
                        type="button"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={2}
                        px={4}
                        py={2.5}
                        borderRadius="lg"
                        bg={brand.primary}
                        color="white"
                        fontSize="sm"
                        fontWeight="700"
                        cursor="pointer"
                        _hover={{ opacity: 0.92 }}
                        onClick={handleDownload}
                      >
                        <LuDownload size={15} />
                        {t('expedientes.electronico.entrada.download')}
                      </Box>
                    </VStack>
                  ) : (
                    <Flex gap={2} alignItems="center" color={themeColors.textMuted}>
                      <LuFileText size={15} />
                      <Text fontSize="xs">{t('expedientes.electronico.entrada.noFile')}</Text>
                    </Flex>
                  )}
                </Box>
              </VStack>
            </Box>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
